import { Button, Stack, Typography } from '@mui/material'
import { Controller } from 'react-hook-form'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { StyledSlider } from '../StyledSlider'

type Props = {
  suffix: '€' | '% p.a.' | 'mesiacov'
  name: string
  control: any
  onChangeCommitted?: () => void
  min: number
  max: number
  step: number
  disabled?: boolean
  hideMinMax?: boolean
  className?: string
}

const valueToSliderPosition = (
  value: number,
  min: number,
  max: number
): number => {
  const minLog = Math.log(min)
  const maxLog = Math.log(max)
  const valueLog = Math.log(value)

  return ((valueLog - minLog) / (maxLog - minLog)) * 100
}

const sliderPositionToValue = (
  position: number,
  min: number,
  max: number
): number => {
  const minLog = Math.log(min)
  const maxLog = Math.log(max)

  const normalizedPosition = position / 100
  const valueLog = minLog + normalizedPosition * (maxLog - minLog)

  return Math.exp(valueLog)
}

const getDynamicStep = (value: number, baseStep: number): number => {
  if (value < 1000) return baseStep
  if (value < 5000) return baseStep * 2
  if (value < 15000) return baseStep * 5
  if (value < 30000) return baseStep * 10
  return baseStep * 10
}

const roundToStep = (
  value: number,
  step: number,
  min: number,
  max: number
): number => {
  const rounded = Math.round(value / step) * step
  return Math.max(min, Math.min(max, rounded))
}

export const OneTuneInvestmentSlider = ({
  suffix,
  name,
  control,
  onChangeCommitted,
  min,
  max,
  step,
  disabled = false,
  hideMinMax,
  className = '',
}: Props) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const dynamicStep = getDynamicStep(value, step)
        const sliderValue = valueToSliderPosition(value, min, max)

        return (
          <Stack className={`gap-[13px] ${className}`}>
            <Stack className="flex-row justify-between items-center pt-0.5">
              <Button
                onClick={() => {
                  const newValue = roundToStep(
                    value - dynamicStep,
                    dynamicStep,
                    min,
                    max
                  )
                  onChange(newValue)
                }}
                disabled={disabled || value <= min}
                className={`w-8 h-8 min-w-[unset] min-h-[unset] p-0 bg-zGrey400 text-zWhite200 ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}>
                <RemoveIcon className="!w-5 !h-5" />
              </Button>
              <Stack className="items-center gap-1">
                <Typography
                  className={`text-6.5d font-black text-zWhite200 tracking-wide`}>
                  {`${value.toLocaleString('sk-SK')} ${suffix}`}
                </Typography>
                {!hideMinMax && (
                  <Stack className="items-center">
                    <Typography
                      className={`text-xs font-semibold text-zGrey400`}>
                      {`Min ${min.toLocaleString()} ${suffix}`}
                    </Typography>
                  </Stack>
                )}
              </Stack>
              <Button
                onClick={() => {
                  const newValue = roundToStep(
                    value + dynamicStep,
                    dynamicStep,
                    min,
                    max
                  )
                  onChange(newValue)
                }}
                disabled={disabled || value >= max}
                className={`w-8 h-8 min-w-[unset] min-h-[unset] p-0 bg-zGrey400 text-zWhite200 ${
                  disabled ? 'cursor-not-allowed' : ''
                }`}>
                <AddIcon className="w-5 h-5" />
              </Button>
            </Stack>

            <Stack className="px-4">
              <StyledSlider
                min={0}
                max={100}
                step={0.1} // Jemný krok pre plynulý pohyb
                value={sliderValue}
                onChange={(_, newSliderValue) => {
                  // Transformujeme slider pozíciu späť na skutočnú hodnotu
                  const realValue = sliderPositionToValue(
                    newSliderValue as number,
                    min,
                    max
                  )
                  // Zaokrúhľujeme na platný krok
                  const currentStep = getDynamicStep(realValue, step)
                  const roundedValue = roundToStep(
                    realValue,
                    currentStep,
                    min,
                    max
                  )
                  onChange(roundedValue)
                }}
                disabled={disabled}
                onChangeCommitted={() => {
                  if (onChangeCommitted) {
                    onChangeCommitted()
                  }
                }}
              />
            </Stack>
          </Stack>
        )
      }}
    />
  )
}
